import { useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Language } from "@/types/enums";
import type { I18nString } from "@/types/common";
import { useImageUpload } from "@/hooks/use-image-upload";
import { ImageIcon, Upload, X, Loader2 } from "lucide-react";

interface ImageUploadFieldProps {
  label: string;
  value: I18nString;
  onChange: (value: I18nString) => void;
  category?: string;
  error?: string;
  required?: boolean;
  placeholder?: string;
}

const DEFAULT_I18N: I18nString = { ko: "", en: null };
const ACCEPT = "image/jpeg,image/png,image/webp,image/gif";

export function ImageUploadField({
  label,
  value,
  onChange,
  category = "post-thumbnail",
  error,
  required = false,
  placeholder = "https://example.com/image.jpg",
}: ImageUploadFieldProps) {
  const [lang, setLang] = useState<Language>(Language.KO);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const safeValue = value ?? DEFAULT_I18N;

  const { upload, isUploading } = useImageUpload({ category });

  const currentUrl =
    lang === Language.KO ? safeValue.ko : (safeValue.en || "");

  const handleUrlChange = (text: string, language: Language) => {
    if (language === Language.KO) {
      onChange({ ...safeValue, ko: text });
    } else {
      onChange({ ...safeValue, en: text || null });
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const url = await upload(file);
    if (url) {
      handleUrlChange(url, lang);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClearUrl = () => {
    handleUrlChange("", lang);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className={error ? "text-destructive" : ""}>
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </Label>
        <Tabs value={lang} onValueChange={(v) => setLang(v as Language)}>
          <TabsList className="h-7">
            <TabsTrigger
              value={Language.KO}
              className="px-2 py-0.5 text-xs"
            >
              KO
            </TabsTrigger>
            <TabsTrigger
              value={Language.EN}
              className="px-2 py-0.5 text-xs"
            >
              EN
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {currentUrl && (
        <div className="relative w-full overflow-hidden rounded-md border">
          <img
            src={currentUrl}
            alt="preview"
            className="h-56 w-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-6 w-6"
            onClick={handleClearUrl}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {!currentUrl && (
        <div
          className="flex h-48 cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed transition-colors hover:border-primary hover:bg-muted/50"
          onClick={() => !isUploading && fileInputRef.current?.click()}
        >
          {isUploading ? (
            <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
          ) : (
            <ImageIcon className="text-muted-foreground h-8 w-8" />
          )}
          <span className="text-muted-foreground text-xs">
            {isUploading
              ? "업로드 중..."
              : "클릭하여 이미지를 업로드하세요"}
          </span>
        </div>
      )}

      <Tabs value={lang} onValueChange={(v) => setLang(v as Language)}>
        <TabsContent value={Language.KO} className="mt-0">
          <div className="flex gap-2">
            <Input
              value={safeValue.ko}
              onChange={(e) => handleUrlChange(e.target.value, Language.KO)}
              placeholder={placeholder}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
            </Button>
          </div>
        </TabsContent>
        <TabsContent value={Language.EN} className="mt-0">
          <div className="flex gap-2">
            <Input
              value={safeValue.en || ""}
              onChange={(e) => handleUrlChange(e.target.value, Language.EN)}
              placeholder={`${placeholder} (English, optional)`}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Upload className="h-4 w-4" />
              )}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={handleFileSelect}
      />

      {error && <p className="text-destructive text-sm">{error}</p>}
    </div>
  );
}
